import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';
import { InviteValidateResponse, RegisterRequest } from '../services/types';
import { USER_KEY } from '../utils/storageKeys';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const token = searchParams.get('token') || '';

  const [invite, setInvite] = useState<InviteValidateResponse | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [expiredEmail, setExpiredEmail] = useState('');
  const [loadingInvite, setLoadingInvite] = useState(true);

  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Redirect already-authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setInviteError('No invitation token found. Please use the link from your invitation email.');
      setLoadingInvite(false);
      return;
    }

    apiClient
      .get<InviteValidateResponse>(`/api/v1/auth/invite/${token}`)
      .then((res) => setInvite(res.data))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        const detailMsg = typeof detail === 'object' ? detail?.message : detail || '';
        if (err.response?.status === 410) {
          if (typeof detail === 'object' && detail?.email) {
            setIsExpired(true);
            setExpiredEmail(detail.email);
            setInviteError('This invitation link has expired.');
          } else if (typeof detailMsg === 'string' && detailMsg.toLowerCase().includes('expired')) {
            setIsExpired(true);
            setInviteError('This invitation link has expired.');
          } else {
            setInviteError('This invitation has already been used. Please log in instead.');
          }
        } else if (err.response?.status === 404) {
          setInviteError('Invitation not found. The link may be invalid.');
        } else {
          setInviteError(detailMsg || 'Failed to validate invitation. Please try again.');
        }
      })
      .finally(() => setLoadingInvite(false));
  }, [token]);

  const handleRequestResend = async () => {
    if (!token) return;
    setResendStatus('loading');
    setResendError(null);
    try {
      await apiClient.post('/api/v1/auth/invite/request-resend', { token });
      setResendStatus('success');
    } catch {
      setResendStatus('error');
      setResendError('Something went wrong. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const body: RegisterRequest = {
        token,
        first_name: firstName,
        last_name: lastName || undefined,
        password,
      };

      // The backend sets auth cookies on this response.
      await apiClient.post('/api/v1/auth/register', body);

      // Cookies are already in place — no Authorization header needed.
      const meRes = await apiClient.get('/api/v1/auth/me');
      localStorage.setItem(USER_KEY, JSON.stringify(meRes.data));

      // Force a full reload so AuthContext picks up the new session.
      window.location.href = '/dashboard';
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 410) {
        setSubmitError('This invitation has already been used or has expired.');
      } else if (err.response?.status === 409) {
        setSubmitError('An account with this email already exists. Please log in instead.');
      } else {
        setSubmitError(detail || 'Registration failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 460,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          background: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/images/migratr.ai L.png"
              alt="migratr"
              sx={{ width: 200, height: 'auto', objectFit: 'contain' }}
            />
          </Box>

          <Typography variant="h5" fontWeight={600} mb={0.5} textAlign="center">
            Create your account
          </Typography>

          {loadingInvite ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : inviteError ? (
            <>
              <Alert severity={isExpired ? 'warning' : 'error'} sx={{ mt: 2 }}>
                {inviteError}
              </Alert>

              {isExpired && resendStatus !== 'success' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    Click below to request a new invite link.
                  </Typography>
                  {expiredEmail && (
                    <TextField
                      fullWidth
                      label="Email address"
                      type="email"
                      value={expiredEmail}
                      disabled
                      size="small"
                      sx={{ mb: 1.5 }}
                    />
                  )}
                  {resendError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>{resendError}</Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleRequestResend}
                    disabled={resendStatus === 'loading'}
                    sx={{ mb: 1.5 }}
                  >
                    {resendStatus === 'loading'
                      ? <CircularProgress size={20} color="inherit" />
                      : 'Request New Invite'}
                  </Button>
                </Box>
              )}

              {isExpired && resendStatus === 'success' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  If a pending invitation exists for this email, a new link has been sent. Please check your inbox.
                </Alert>
              )}

              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: isExpired ? 1 : 3 }}
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                mb={3}
              >
                Invited as <strong>{invite?.email}</strong>
                {invite?.tenant_name ? ` · ${invite.tenant_name}` : ''}
              </Typography>

              <form onSubmit={handleSubmit}>
                {submitError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="First Name"
                    fullWidth
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={submitting}
                    autoFocus
                  />
                  <TextField
                    label="Last Name"
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={submitting}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={invite?.email || ''}
                  disabled
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  required
                  sx={{ mb: 2 }}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={submitting}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={submitting}
                  required
                  sx={{ mb: 3 }}
                  autoComplete="new-password"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || !firstName || !password || !confirmPassword}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              </form>
            </>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            © 2026 Aventior. All rights reserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RegisterPage;
